/** Custom field catalog admin — P5 of ADDRESS_AND_CUSTOM_FIELDS.
 *
 * Two modes, one component:
 *  • Org catalog (no `campaign` prop) — lists system + org-scoped
 *    FieldDefinitions. Admins create org-scoped rows; system rows are
 *    read-only (org overrides them by creating a row with the same
 *    `key`+`applies_to`), and can be disabled/re-enabled per org.
 *  • Campaign catalog (`campaign` set) — lists only that campaign's own
 *    fields. New rows are campaign-scoped: they layer over the org
 *    catalog for that campaign (a campaign field with the same key wins;
 *    new keys are added). System customise/disable is an org-level
 *    concern, so those affordances don't appear here.
 *
 * Per-row Edit shipped 2026-09-18 (an operator asked): reopens the
 * create form pre-filled, PATCHing an org-owned row in place instead
 * of archive-and-recreate. `key`/`kind`/`applies_to` stay locked in
 * edit mode — changing any of them after the field has data would
 * orphan or mistype whatever's already stored under it. Filters by
 * applies_to/country are still parked until an operator asks — system
 * seeds cover the canonical EU identity fields already.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CountrySelector } from "@/components/CountrySelector";
import {
  archiveFieldDefinition,
  createFieldDefinition,
  listFieldDefinitions,
  patchFieldDefinition,
  systemToggleField,
  type FieldDefinitionWritePayload,
} from "@/modules/campaigns/api";
import type {
  FieldAppliesTo,
  FieldDefinition,
  FieldKind,
} from "@/lib/types";

const APPLIES_TO_OPTIONS: FieldAppliesTo[] = [
  "contact",
  "address",
  "membership",
  "party",
];

const KIND_OPTIONS: FieldKind[] = [
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "dropdown",
  "checkboxes",
  "email",
  "regex",
];

const EMPTY_DRAFT: FieldDefinitionWritePayload = {
  key: "",
  label: "",
  help_text: "",
  kind: "text",
  applies_to: "contact",
  country_scope: "",
  is_required: false,
  is_pii: false,
};

export function CustomFieldsCard({ campaign }: { campaign?: string } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Campaign custom data is per-beneficiary-per-campaign → default to
  // `membership` (it flows into the import template + beneficiary detail).
  // "party" is session-only (signatories) and never materialises in the
  // beneficiary import, so it's hidden in campaign mode to avoid the trap of
  // creating a campaign field that then can't be imported.
  const defaultAppliesTo: FieldAppliesTo = campaign ? "membership" : "contact";
  const appliesToOptions: FieldAppliesTo[] = campaign
    ? ["membership", "contact", "address"]
    : APPLIES_TO_OPTIONS;
  const emptyDraft: FieldDefinitionWritePayload = {
    ...EMPTY_DRAFT,
    applies_to: defaultAppliesTo,
  };
  const [draft, setDraft] = useState<FieldDefinitionWritePayload>(emptyDraft);
  const [showAdd, setShowAdd] = useState(false);
  // When set, the create form is seeding an org override for this system
  // field (same key + applies_to wins over the platform row). Org-mode only.
  const [customizing, setCustomizing] = useState<FieldDefinition | null>(null);
  // When set, the form PATCHes this existing org-owned row instead of
  // POSTing a new one (2026-09-18: the backend already had a working
  // PATCH — nothing in the UI called it, so fixing a label typo meant
  // archiving the field and losing continuity with existing data).
  const [editingId, setEditingId] = useState<string | null>(null);

  // Campaign mode lists only that campaign's own fields; org mode lists
  // the org catalog + system rows. Keyed separately so the two caches
  // never collide.
  const queryKey = campaign
    ? (["fieldDefinitions", "campaign", campaign] as const)
    : (["fieldDefinitions"] as const);

  const list = useQuery({
    queryKey,
    queryFn: () =>
      listFieldDefinitions(
        campaign ? { campaign, include_system: false } : {},
      ),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createFieldDefinition(campaign ? { ...draft, campaign } : draft),
    onSuccess: () => {
      setDraft(emptyDraft);
      setShowAdd(false);
      setCustomizing(null);
      qc.invalidateQueries({ queryKey });
    },
  });

  // Pre-fill the create form from a system field so the admin can tweak
  // and save an org override in one step (no manual re-typing of
  // key/applies_to, which must match for the override to take effect).
  function startCustomize(row: FieldDefinition) {
    setDraft({
      key: row.key,
      label: row.label,
      help_text: row.help_text,
      kind: row.kind,
      applies_to: row.applies_to,
      country_scope: row.country_scope,
      project_category_scope: row.project_category_scope,
      role_scope: row.role_scope,
      is_required: row.is_required,
      is_pii: row.is_pii,
      options_jsonb: row.options_jsonb,
      regex_pattern: row.regex_pattern,
      min_value: row.min_value,
      max_value: row.max_value,
    });
    setCustomizing(row);
    setShowAdd(true);
  }

  // Edit an existing org-owned row in place. `key`/`kind`/`applies_to`
  // stay locked in the form below — changing any of them after the
  // field has data would orphan values stored under the old key (or
  // leave a `kind` mismatch, e.g. text data under a field now typed
  // dropdown). Everything else (label, help text, required/pii,
  // options, regex, scopes) is safe to change going forward.
  function startEdit(row: FieldDefinition) {
    setDraft({
      key: row.key,
      label: row.label,
      help_text: row.help_text,
      kind: row.kind,
      applies_to: row.applies_to,
      country_scope: row.country_scope,
      project_category_scope: row.project_category_scope,
      role_scope: row.role_scope,
      is_required: row.is_required,
      is_pii: row.is_pii,
      options_jsonb: row.options_jsonb,
      regex_pattern: row.regex_pattern,
      min_value: row.min_value,
      max_value: row.max_value,
    });
    setEditingId(row.id);
    setCustomizing(null);
    setShowAdd(true);
  }

  const updateMut = useMutation({
    mutationFn: () => patchFieldDefinition(editingId!, draft),
    onSuccess: () => {
      setDraft(emptyDraft);
      setShowAdd(false);
      setEditingId(null);
      qc.invalidateQueries({ queryKey });
    },
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveFieldDefinition(id),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  // #04 — org opt-out of a system-provided field (org mode only).
  const systemToggleMut = useMutation({
    mutationFn: (v: { key: string; applies_to: string; disabled: boolean }) =>
      systemToggleField(v.key, v.applies_to, v.disabled),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  if (list.isLoading) {
    return <div className="card text-sm text-nb-text-2">{t("admin.fields.loading")}</div>;
  }

  const rows = list.data ?? [];
  // Collapse system/override pairs: when the org has its own row for a
  // (key, applies_to), hide the platform row and show only the effective
  // (org) definition — that's what actually applies.
  const orgOwned = new Set(
    rows
      .filter((r) => !r.is_system)
      .map((r) => `${r.key}|${r.applies_to}`),
  );
  const displayRows = rows.filter(
    (r) => !(r.is_system && orgOwned.has(`${r.key}|${r.applies_to}`)),
  );

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          {campaign
            ? t("admin.fields.campaignTitle", "Campaign custom fields")
            : t("admin.fields.pageTitle")}
        </h2>
        <button
          type="button"
          className="btn btn-primary text-sm"
          onClick={() => {
            // Always open a blank "Add" form, even if a customize draft
            // was loaded. `emptyDraft` carries the campaign-aware default
            // scope (membership in campaign mode).
            setDraft(emptyDraft);
            setCustomizing(null);
            setEditingId(null);
            setShowAdd((v) => !v);
          }}
        >
          {t("admin.fields.addButton")}
        </button>
      </div>

      {campaign && (
        <p className="rounded bg-nb-mint/10 px-2 py-1.5 text-xs text-nb-text-2">
          {t(
            "admin.fields.campaignHint",
            "Fields defined here apply only to this campaign. A field with the same key as an org field overrides it for this campaign; new keys are added on top. They flow into this campaign's import template and beneficiary forms.",
          )}
        </p>
      )}

      {showAdd && (
        <form
          className="grid grid-cols-2 gap-3 rounded border border-nb-border bg-nb-surface/40 p-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (editingId) updateMut.mutate();
            else createMut.mutate();
          }}
        >
          {customizing && (
            <p className="col-span-2 rounded bg-nb-mint/10 px-2 py-1.5 text-xs text-nb-text-2">
              {t(
                "admin.fields.customizeHint",
                "Creating an organization override for the system field “{{key}}”. Keep the key and “applies to” unchanged so it overrides the platform definition; edit the rest as needed.",
                { key: customizing.key },
              )}
            </p>
          )}
          {editingId && (
            <p className="col-span-2 rounded bg-nb-mint/10 px-2 py-1.5 text-xs text-nb-text-2">
              {t(
                "admin.fields.editHint",
                "Editing “{{key}}”. Key, kind, and “applies to” can't be changed here — they'd orphan or mistype any data already stored under this field.",
                { key: draft.key },
              )}
            </p>
          )}
          <label className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.key")}
            </span>
            <input
              className="input mt-1 w-full"
              value={draft.key ?? ""}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              required
              pattern="^[a-z][a-z0-9_]*$"
              disabled={!!editingId}
            />
          </label>
          <label className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.label")}
            </span>
            <input
              className="input mt-1 w-full"
              value={draft.label ?? ""}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              required
            />
          </label>
          <label className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.kind")}
            </span>
            <select
              className="input mt-1 w-full"
              value={draft.kind}
              onChange={(e) =>
                setDraft({ ...draft, kind: e.target.value as FieldKind })
              }
              disabled={!!editingId}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {t(`admin.fields.kindOption.${k}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.appliesTo")}
            </span>
            <select
              className="input mt-1 w-full"
              value={draft.applies_to}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  applies_to: e.target.value as FieldAppliesTo,
                })
              }
              disabled={!!editingId}
            >
              {appliesToOptions.map((a) => (
                <option key={a} value={a}>
                  {t(`admin.fields.appliesToOption.${a}`)}
                </option>
              ))}
            </select>
            {/* 2026-09-28 — the hint used to describe `membership` and
                only `membership`, whatever was selected, which made the
                one distinction that matters invisible: a value saved
                once per BENEFICIARY versus once per SITE. A campaign
                was imported with a per-site column ("nombre
                d'opérations", a different number for each of a
                beneficiary's worksites) mapped to a membership field,
                so every row after the first overwrote the one before
                and only a single number survived. The option names
                alone ("Adhésion", "Adresse") cannot carry that. */}
            <span className="mt-1 block text-[11px] text-nb-text-3">
              {t(`admin.fields.appliesToHint.${draft.applies_to}`)}
            </span>
          </label>
          <div className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.country")}
            </span>
            {/* Scope filter: blank = applies to every country. */}
            <div className="mt-1">
              <CountrySelector
                value={draft.country_scope ?? ""}
                onChange={(code) =>
                  setDraft({ ...draft, country_scope: code })
                }
                allowEmpty
                placeholder={t("admin.fields.countryAny", "Any country")}
              />
            </div>
          </div>
          <label className="col-span-1">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.regex")}
            </span>
            <input
              className="input mt-1 w-full font-mono text-xs"
              value={draft.regex_pattern ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, regex_pattern: e.target.value })
              }
            />
          </label>
          <label className="col-span-1 flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!draft.is_required}
              onChange={(e) =>
                setDraft({ ...draft, is_required: e.target.checked })
              }
            />
            <span>{t("admin.fields.required")}</span>
          </label>
          <label className="col-span-1 flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!draft.is_pii}
              onChange={(e) =>
                setDraft({ ...draft, is_pii: e.target.checked })
              }
            />
            <span>{t("admin.fields.pii")}</span>
          </label>
          <label className="col-span-2">
            <span className="block text-xs text-nb-text-2">
              {t("admin.fields.helpText")}
            </span>
            <input
              className="input mt-1 w-full"
              value={draft.help_text ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, help_text: e.target.value })
              }
            />
          </label>

          {(draft.kind === "dropdown" || draft.kind === "checkboxes") && (
            <div className="col-span-2 space-y-2 rounded border border-nb-border bg-nb-bg/40 p-2">
              <p className="text-xs font-medium text-nb-text-2">
                {t("admin.fields.options")}
              </p>
              <OptionsEditor
                options={
                  Array.isArray(draft.options_jsonb)
                    ? (draft.options_jsonb as Array<{
                        value: string;
                        label: string;
                      }>)
                    : []
                }
                onChange={(opts) =>
                  setDraft({ ...draft, options_jsonb: opts })
                }
              />
            </div>
          )}
          {(editingId ? updateMut.isError : createMut.isError) && (
            <p className="col-span-2 text-xs text-cvp-danger">
              {String(editingId ? updateMut.error : createMut.error)}
            </p>
          )}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn btn-ghost text-sm"
              onClick={() => {
                setDraft(emptyDraft);
                setCustomizing(null);
                setEditingId(null);
                setShowAdd(false);
              }}
            >
              {t("admin.fields.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary text-sm"
              disabled={editingId ? updateMut.isPending : createMut.isPending}
            >
              {editingId
                ? updateMut.isPending
                  ? t("common.saving")
                  : t("admin.fields.saveChanges", "Save changes")
                : createMut.isPending
                  ? t("admin.fields.creating")
                  : t("admin.fields.save")}
            </button>
          </div>
        </form>
      )}

      {displayRows.length === 0 ? (
        <p className="text-sm text-nb-text-2">
          {t("admin.fields.emptyState")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-nb-text-2">
              <tr>
                <th className="px-2 py-2">{t("admin.fields.key")}</th>
                <th className="px-2 py-2">{t("admin.fields.label")}</th>
                <th className="px-2 py-2">{t("admin.fields.kind")}</th>
                <th className="px-2 py-2">{t("admin.fields.appliesTo")}</th>
                <th className="px-2 py-2">{t("admin.fields.country")}</th>
                <th className="px-2 py-2">{t("admin.fields.required")}</th>
                <th className="px-2 py-2">{t("admin.fields.pii")}</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-nb-border/40 align-top"
                >
                  <td className="px-2 py-2 font-mono text-xs">
                    {row.key}
                    {row.is_system && (
                      <span className="ml-1 rounded bg-nb-surface px-1 text-[10px] text-nb-text-2">
                        {t("admin.fields.system")}
                      </span>
                    )}
                    {row.is_org_override && !row.is_disabled && (
                      <span className="ml-1 rounded bg-emerald-500/20 px-1 text-[10px] text-emerald-200">
                        {t("admin.fields.override")}
                      </span>
                    )}
                    {row.is_disabled && (
                      <span className="ml-1 rounded bg-cvp-danger/20 px-1 text-[10px] text-cvp-danger">
                        {t("admin.fields.disabled", "disabled")}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">{row.label}</td>
                  <td className="px-2 py-2">
                    {t(`admin.fields.kindOption.${row.kind}`, row.kind)}
                  </td>
                  <td className="px-2 py-2">
                    {t(`admin.fields.appliesToOption.${row.applies_to}`, row.applies_to)}
                  </td>
                  <td className="px-2 py-2">{row.country_scope || "—"}</td>
                  <td className="px-2 py-2">
                    {row.is_required ? "✓" : ""}
                  </td>
                  <td className="px-2 py-2">{row.is_pii ? "✓" : ""}</td>
                  <td className="space-x-3 px-2 py-2 text-right">
                    {row.is_disabled ? (
                      <button
                        type="button"
                        className="text-xs text-nb-mint hover:underline"
                        onClick={() =>
                          systemToggleMut.mutate({
                            key: row.key,
                            applies_to: row.applies_to,
                            disabled: false,
                          })
                        }
                      >
                        {t("admin.fields.enable", "Enable")}
                      </button>
                    ) : (
                      <>
                        {row.is_system && (
                          <button
                            type="button"
                            className="text-xs text-nb-mint hover:underline"
                            onClick={() => startCustomize(row)}
                          >
                            {t("admin.fields.customize", "Customize")}
                          </button>
                        )}
                        {row.is_system && (
                          <button
                            type="button"
                            className="text-xs text-cvp-danger hover:underline"
                            onClick={() => {
                              if (
                                window.confirm(
                                  t(
                                    "admin.fields.disableConfirm",
                                    "Remove this system field for your organisation? You can re-enable it later.",
                                  )
                                )
                              ) {
                                systemToggleMut.mutate({
                                  key: row.key,
                                  applies_to: row.applies_to,
                                  disabled: true,
                                });
                              }
                            }}
                          >
                            {t("admin.fields.disable", "Disable")}
                          </button>
                        )}
                        {!row.is_system && row.archived_at === null && (
                          <button
                            type="button"
                            className="text-xs text-nb-mint hover:underline"
                            onClick={() => startEdit(row)}
                          >
                            {t("admin.fields.edit", "Edit")}
                          </button>
                        )}
                        {!row.is_system && row.archived_at === null && (
                          <button
                            type="button"
                            className="text-xs text-cvp-danger hover:underline"
                            onClick={() => {
                              if (
                                window.confirm(t("admin.fields.deleteConfirm"))
                              ) {
                                archiveMut.mutate(row.id);
                              }
                            }}
                          >
                            {t("admin.fields.archive")}
                          </button>
                        )}
                        {row.archived_at !== null && (
                          <span className="text-xs text-nb-text-2">
                            {t("admin.fields.archived")}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


type Opt = { value: string; label: string };

function OptionsEditor({
  options,
  onChange,
}: {
  options: Opt[];
  onChange: (opts: Opt[]) => void;
}) {
  const { t } = useTranslation();
  function setAt(i: number, next: Opt) {
    onChange(options.map((o, idx) => (idx === i ? next : o)));
  }
  function removeAt(i: number) {
    onChange(options.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...options, { value: "", label: "" }]);
  }
  return (
    <div className="space-y-1">
      {options.length === 0 && (
        <p className="text-[10px] text-nb-text-3">
          {t(
            "admin.fields.optionsEmpty",
            "No options yet. Add at least one (value + label).",
          )}
        </p>
      )}
      {options.map((opt, i) => (
        <div key={i} className="flex gap-2">
          <input
            className="input flex-1 font-mono text-xs"
            placeholder={t("admin.fields.optionValuePlaceholder")}
            value={opt.value}
            onChange={(e) =>
              setAt(i, { ...opt, value: e.target.value })
            }
          />
          <input
            className="input flex-[2] text-xs"
            placeholder={t("admin.fields.optionLabelPlaceholder")}
            value={opt.label}
            onChange={(e) =>
              setAt(i, { ...opt, label: e.target.value })
            }
          />
          <button
            type="button"
            className="text-xs text-cvp-danger hover:underline"
            onClick={() => removeAt(i)}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={add}
      >
        + {t("admin.fields.optionsAdd", "Add option")}
      </button>
    </div>
  );
}
